import { MAX_AGE_CAT, MAX_AGE_DOG } from "components-shared/insuranceData";
import { selectCurrentInsuranceByAnimalId } from "components/Signup/store/selectors/insurance";
import { selectHasDrafts } from "../store/selectors/animals";
import { isAfter, isBefore, isSameDay, subMonths } from "date-fns";
import {
  getEarliestStartDate,
  getLastAllowedStartDate,
  userAbove18,
} from "utils/insurance";
import {
  dateValid,
  dateValidMax,
  dateValidMin,
  frenchZipValid,
  validateEmail,
  validatePhone,
} from "utils/validation";
import { SpeciesEnum } from "../../../../__generated__/globalTypes";
import { Step } from "./types";
import { selectCustomer } from "../store/selectors/customer";

export const stepsFra: Step[] = [
  {
    index: 1,
    name: "species",
    isValid: ({ animal }) => Boolean(animal?.species),
    next: () => "name",
  },
  {
    index: 2,
    name: "name",
    isValid: ({ animal }) => Boolean(animal?.name),
    next: () => "breed",
  },
  {
    index: 3,
    name: "breed",
    isValid: ({ animal }) => Boolean(animal?.breed),
    next: ({ animal }) => {
      return animal?.breed?.id === "DogBreed-blandras"
        ? "mixed-breed"
        : "animal-sex";
    },
  },
  {
    index: 4,
    name: "mixed-breed",
    next: () => "animal-sex",
    isValid: ({ animal }) => {
      if (!animal) return false;

      const { breed, mixedBreedFHalf, mixedBreedSHalf } = animal;
      return breed?.id === "DogBreed-blandras"
        ? !!mixedBreedFHalf && !!mixedBreedSHalf
        : !!breed;
    },
  },
  {
    index: 5,
    name: "animal-sex",
    next: () => "castration",
    isValid: ({ animal }) => Boolean(animal?.sex),
  },
  {
    index: 6,
    name: "castration",
    next: ({ animal }) =>
      animal?.species === SpeciesEnum.cat ? "animal-access" : "animal-birthday",
    isValid: ({ animal }) => Boolean(animal?.sexSituation),
  },
  {
    index: 7,
    name: "animal-access",
    next: () => "animal-birthday",
    isValid: ({ animal }) => {
      if (!animal) return false;

      const { species, access } = animal;

      return species === SpeciesEnum.cat ? !!access : true;
    },
  },
  {
    index: 8,
    name: "animal-birthday",
    next: () => "address",
    isValid: ({ animal }) => {
      if (!animal) return false;

      const { birthday, species } = animal;

      if (!birthday) {
        return false;
      }

      return (
        dateValid(birthday) &&
        dateValidMin(
          new Date(birthday),
          subMonths(
            new Date(),
            species === SpeciesEnum.dog ? MAX_AGE_DOG : MAX_AGE_CAT
          )
        ) &&
        dateValidMax(new Date(birthday), new Date())
      );
    },
  },
  {
    index: 9,
    name: "address",
    next: () => "email",
    isValid: (state) => {
      const { address, region } = selectCustomer(state);

      return frenchZipValid(address?.zip ?? "") && !!region;
    },
  },
  {
    index: 10,
    name: "email",
    next: () => "summary",
    isValid: (state) => {
      const { email, phone } = selectCustomer(state);

      const validEmail =
        email !== "" && email !== null ? validateEmail(email) : true;
      const validPhone =
        phone !== "" && phone !== null ? validatePhone(phone) : true;

      return validEmail && validPhone;
    },
  },
  {
    index: 11,
    name: "summary",
    isValid: () => true,
    next: () => "loading",
  },
  {
    index: 11,
    name: "loading",
    next: () => "quote-preview",
    isValid: (state) => selectHasDrafts(state),
  },
  {
    index: 12,
    name: "quote-preview",
    isValid: ({ animal, ...state }) => {
      if (!animal) return false;

      const { limit, deductible, startDate } = selectCurrentInsuranceByAnimalId(
        state,
        animal.id
      );
      const { birthday } = animal;

      if (!startDate || !birthday) {
        return false;
      }

      const startDateValid =
        dateValid(startDate) &&
        (isSameDay(new Date(startDate), getEarliestStartDate(birthday)) ||
          (isAfter(new Date(startDate), getEarliestStartDate(birthday)) &&
            isBefore(new Date(startDate), getLastAllowedStartDate())));

      return !!limit && (!!deductible || deductible === 0) && startDateValid;
    },
    next: () => "preventive",
    previous: (state) => (state.metadata.isResumed ? "summary" : null),
  },
  {
    index: 13,
    name: "preventive",
    isValid: () => true,
    next: () => "health-questions",
  },
  {
    index: 13,
    name: "health-questions",
    next: () => "user-info",
    isValid: ({ animal }) => {
      if (!animal) return false;

      return animal.healthHistory.length > 0;
    },
  },
  {
    index: 14,
    name: "user-info",
    next: () => "draft-summary",
    isValid: (state) => {
      const {
        email,
        phone,
        firstName,
        lastName,
        birthdate,
        address,
        privacyAccepted,
      } = selectCustomer(state);

      if (!birthdate) {
        return false;
      }

      return !!(
        validateEmail(email) &&
        validatePhone(phone) &&
        firstName &&
        lastName &&
        userAbove18(birthdate) &&
        address?.line1 &&
        address.city &&
        privacyAccepted
      );
    },
  },
  {
    index: 15,
    name: "draft-summary",
    isValid: () => true,
    next: () => "payment",
  },
  {
    index: 15,
    name: "payment",
    next: () => "thank-you",
    isValid: () => true,
  },
  {
    index: 16,
    name: "thank-you",
    isValid: () => true,
    next: () => "thank-you",
  },
];
